import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Button, Field, Input, Textarea, Card, StatusPill, Select, Modal } from "@/components/kit";
import { useQuickAdd } from "@/lib/quickAdd";
import ConfirmDelete from "@/components/ConfirmDelete";
import { fmtDate, fmtCt, parseLocalDate } from "@/lib/dates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BOARD = [
  ["researching", "Researching"], ["drafting", "Drafting"], ["submitted", "Submitted"],
  ["awarded", "Awarded"], ["active_reporting", "Active & reporting"], ["closed", "Closed"],
];
const ALL_STAGES = [...BOARD, ["declined", "Declined"]];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;
// FastAPI 422 details are arrays; only show plain-text messages in toasts.
const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : ""; };
const fmt = (s) => fmtDate(s); // local-time parse — fixes the day/year-off timezone bug
const STAGE_LABELS = { researching: "Researching", drafting: "Drafting", submitted: "Submitted", awarded: "Awarded", declined: "Declined", active_reporting: "Active & reporting", closed: "Closed" };
const SUBMITTED_OR_LATER = ["submitted", "awarded", "active_reporting", "closed"];
// The date that actually matters at each stage, with its label (not the entry date).
function primaryStageDate(g) {
  switch (g.stage) {
    case "researching": return { label: "Opened", value: g.open_date, isDue: false };
    case "drafting":
      if (g.submission_date) return { label: "Application due", value: g.submission_date, isDue: true };
      if (g.loi_date) return { label: "LOI due", value: g.loi_date, isDue: true };
      return { label: "Application due", value: "", isDue: true };
    case "submitted": return { label: "Submitted", value: g.stage_dates?.submitted, isDue: false, isTs: true };
    case "awarded": return { label: "Awarded", value: g.stage_dates?.awarded, isDue: false, isTs: true };
    case "declined": return { label: "Declined", value: g.stage_dates?.declined, isDue: false, isTs: true };
    case "active_reporting": return { label: "Report due", value: g.deadline, isDue: true };
    case "closed": return { label: "Closed", value: g.stage_dates?.closed, isDue: false, isTs: true };
    default: return { label: "", value: "", isDue: false };
  }
}

function PersonSearch({ placeholder, endpoint, mapItem, onPick }) {
  const [q, setQ] = useState(""); const [res, setRes] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setRes([]); return; }
    let live = true;
    const t = setTimeout(async () => { try { const r = await api.get(endpoint, { params: { q } }); if (live) setRes(mapItem(r.data)); } catch { /* noop */ } }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, endpoint, mapItem]);
  return (
    <div>
      <Input data-testid="person-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
      {res.length > 0 && (
        <div className="border border-ink/15 rounded-lg mt-1 bg-cream max-h-48 overflow-y-auto">
          {res.map((r) => (
            <button key={`${r.kind}-${r.id}`} data-testid={`person-opt-${r.id}`} onClick={() => { onPick(r); setQ(""); setRes([]); }}
              className="w-full text-left px-3 py-2 border-b border-ink/8 last:border-0 hover:bg-warm-cream/60 text-[14px]">
              {r.name} {r.sub && <span className="text-ink/45 text-[12px]">· {r.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function LeadPicker({ leadRef, leadName, onChange, disabled }) {
  const [mode, setMode] = useState(null);
  const [nf, setNf] = useState({ name: "", email: "", phone: "", organization: "" });
  const name = leadRef?.name || leadName;
  if (name && !mode) return (
    <div className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2" data-testid="lead-chip">
      <span className="text-[14px] font-semibold">{name}{leadRef?.kind === "visitor" ? " · Contact" : leadRef?.kind === "member" ? " · In system" : ""}</span>
      {!disabled && <button data-testid="lead-change" onClick={() => setMode("search")} className="text-[13px] text-dark-olive font-semibold">Change</button>}
    </div>
  );
  if (disabled) return <span className="text-ink/50 text-[14px]">No lead assigned</span>;
  return (
    <div className="space-y-2">
      {mode !== "new" && <PersonSearch placeholder="Search people already in the system…" endpoint="/partnerships/people/search" mapItem={(d) => d.people || []} onPick={(p) => { onChange({ kind: p.kind, id: p.id, name: p.name }); setMode(null); }} />}
      {mode === "new" ? (
        <div className="space-y-2 bg-warm-cream border border-ink/15 rounded-lg p-3" data-testid="lead-new-form">
          <Input data-testid="lead-new-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Full name" />
          <Input data-testid="lead-new-email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email (optional)" />
          <Input data-testid="lead-new-phone" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} placeholder="Phone (optional)" />
          <div className="flex gap-2">
            <Button data-testid="lead-new-save" onClick={() => { if (!nf.name.trim()) return toast.error("Name is required"); onChange({ kind: "new", ...nf }); setMode(null); setNf({ name: "", email: "", phone: "", organization: "" }); }}>Add contact</Button>
            <Button variant="secondary" onClick={() => setMode(null)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <button data-testid="lead-add-new" onClick={() => setMode("new")} className="text-[13px] text-dark-olive font-semibold">+ Not in the system? Add a new contact</button>
      )}
    </div>
  );
}

function CollaboratorPicker({ value, onChange, disabled }) {
  const list = value || [];
  const [adding, setAdding] = useState(false);
  const [newOrg, setNewOrg] = useState(false);
  const [nf, setNf] = useState({ name: "", email: "", phone: "" });
  const add = (c) => { if (!list.some((x) => x.id === c.id)) onChange([...list, c]); setAdding(false); };
  const createOrg = async () => {
    if (!nf.name.trim()) return toast.error("Organization name is required");
    try { const r = await api.post("/partnerships", { name: nf.name, email: nf.email, phone: nf.phone }); add({ id: r.data.id, name: r.data.name }); setNewOrg(false); setNf({ name: "", email: "", phone: "" }); toast.success("Partner added"); }
    catch (e) { toast.error(errText(e) || "Could not add"); }
  };
  return (
    <div className="space-y-2" data-testid="collaborators">
      {list.map((c) => (
        <div key={c.id} className="flex items-center justify-between gap-2 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2" data-testid={`collab-${c.id}`}>
          <span className="text-[14px] font-semibold">{c.name}</span>
          {!disabled && <button data-testid={`collab-remove-${c.id}`} onClick={() => onChange(list.filter((x) => x.id !== c.id))} className="text-ink/40 hover:text-maroon"><Icon name="X" className="w-4 h-4" /></button>}
        </div>
      ))}
      {list.length === 0 && <p className="text-[13px] text-ink/45">No collaborating organizations yet.</p>}
      {!disabled && !adding && !newOrg && <button data-testid="collab-add" onClick={() => setAdding(true)} className="text-[13px] text-dark-olive font-semibold">+ Add a collaborating organization</button>}
      {adding && !newOrg && (
        <div className="space-y-2">
          <PersonSearch placeholder="Search partner organizations…" endpoint="/partnerships" mapItem={(d) => (d.items || []).map((i) => ({ kind: "org", id: i.id, name: i.name, sub: i.partner_type || "" }))} onPick={(p) => add({ id: p.id, name: p.name })} />
          <div className="flex gap-3"><button data-testid="collab-new" onClick={() => setNewOrg(true)} className="text-[13px] text-dark-olive font-semibold">+ Not in Partnerships? Add new</button><button onClick={() => setAdding(false)} className="text-[13px] text-ink/50">Cancel</button></div>
        </div>
      )}
      {newOrg && (
        <div className="space-y-2 bg-warm-cream border border-ink/15 rounded-lg p-3" data-testid="collab-new-form">
          <Input data-testid="collab-new-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Organization name" />
          <Input data-testid="collab-new-email" value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} placeholder="Email (optional)" />
          <Input data-testid="collab-new-phone" value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} placeholder="Phone (optional)" />
          <div className="flex gap-2"><Button data-testid="collab-new-save" onClick={createOrg}>Add partner</Button><Button variant="secondary" onClick={() => setNewOrg(false)}>Cancel</Button></div>
        </div>
      )}
    </div>
  );
}

function GrantCard({ g, onOpen, selectable, selected, onToggle }) {
  const prim = primaryStageDate(g);
  const entered = g.stage_dates?.[g.stage];
  return (
    <div data-testid={`grant-card-wrap-${g.grant_id}`} className={`relative bg-cream border rounded-lg p-3 hover:border-maroon/40 transition-colors ${selected ? "border-maroon ring-1 ring-maroon/40" : "border-ink/15"}`}>
      {selectable && <input type="checkbox" data-testid={`grant-select-${g.grant_id}`} checked={!!selected} onChange={onToggle} onClick={(e) => e.stopPropagation()} className="absolute top-2 right-2 w-4 h-4 accent-maroon" />}
      <button data-testid={`grant-card-${g.grant_id}`} onClick={() => onOpen(g.grant_id)} className="w-full text-left">
        <div className="font-semibold text-[14px] leading-snug pr-5">{g.title}</div>
        <div className="text-[12px] text-ink/55 mb-2">{g.funder || "—"}</div>
        <div className="flex items-center justify-between gap-2">
          <span className="font-zilla text-lg font-semibold">{money(g.amount_awarded || g.amount_requested)}</span>
          {prim.value
            ? <StatusPill tone={prim.isDue ? "gold" : "gray"} data-testid={`grant-primary-date-${g.grant_id}`}>{prim.label} {(prim.isTs ? fmtCt : fmtDate)(prim.value)}</StatusPill>
            : prim.isDue ? <StatusPill tone="gray" data-testid={`grant-primary-date-${g.grant_id}`}>No due date set</StatusPill> : null}
        </div>
        {entered && <div className="text-[11px] text-ink/45 mt-1" data-testid={`grant-entered-${g.grant_id}`}>Entered {STAGE_LABELS[g.stage]} · {fmtCt(entered)}</div>}
        {g.lead && <div className="text-[11px] text-ink/50 mt-1.5">Lead: {g.lead}</div>}
      </button>
    </div>
  );
}

function OpenRecord({ grantId, canEdit, onBack, onChanged }) {
  const [g, setG] = useState(null);
  const [f, setF] = useState(null);
  const [note, setNote] = useState("");
  const [noteAt, setNoteAt] = useState("");
  const [editNote, setEditNote] = useState(null);
  const [secLabel, setSecLabel] = useState("");
  const [addingSec, setAddingSec] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const dropRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get("/grants");
    const found = r.data.items.find((x) => x.grant_id === grantId);
    setG(found); setF(found ? { ...found } : null);
  }, [grantId]);
  useEffect(() => { load(); }, [load]);
  if (!g || !f) return <div className="py-16 text-center text-ink/55">Loading…</div>;

  const save = async (patch) => {
    const body = { ...f, ...patch };
    try { const r = await api.put(`/grants/${grantId}`, body); setG(r.data); setF({ ...r.data }); onChanged && onChanged(); return true; }
    catch (e) { toast.error(errText(e) || "Could not save"); return false; }
  };
  const del = async () => {
    try { await api.delete(`/grants/${grantId}`); toast.success("Grant moved to Trash"); onChanged && onChanged(); onBack(); }
    catch (e) { toast.error("Could not delete"); }
  };
  const setStage = async (stage) => { if (await save({ stage })) toast.success(`Moved to ${ALL_STAGES.find((s) => s[0] === stage)[1]}`); };
  const setNarr = (i, text) => setF({ ...f, narrative: (f.narrative || []).map((s, j) => (j === i ? { ...s, text } : s)) });
  const addSection = () => { const label = secLabel.trim(); if (!label) return; setF({ ...f, narrative: [...(f.narrative || []), { key: `custom_${Date.now()}`, label, text: "" }] }); setSecLabel(""); setAddingSec(false); };
  const delSection = (i) => { const next = (f.narrative || []).filter((_, j) => j !== i); setF({ ...f, narrative: next }); save({ narrative: next }); };
  const setFC = (i, k, v) => setF({ ...f, funder_contacts: (f.funder_contacts || []).map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  const addNote = async () => { if (!note.trim()) return; try { await api.post(`/grants/${grantId}/notes`, { text: note, at: noteAt || undefined }); setNote(""); setNoteAt(""); load(); } catch (e) { toast.error("Could not add note"); } };
  const saveNote = async () => { try { await api.put(`/grants/${grantId}/notes/${editNote.id}`, { text: editNote.text, at: editNote.at || undefined }); setEditNote(null); load(); } catch (e) { toast.error("Could not save"); } };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/grants/${grantId}/documents`, fd); toast.success("Uploaded"); load(); }
    catch (e) { toast.error(errText(e) || "Upload failed"); }
  };
  const onDrop = (e) => { e.preventDefault(); dropRef.current?.classList.remove("border-maroon"); const file = e.dataTransfer.files?.[0]; if (file) upload(file); };

  return (
    <div data-testid="grant-record">
      <button onClick={onBack} data-testid="grant-back" className="text-[14px] text-dark-olive font-semibold mb-3 flex items-center gap-1"><Icon name="ChevronRight" className="w-4 h-4 rotate-180" /> Back to Grant Portal</button>
      {canEdit && <div className="mb-3"><Button variant="danger" data-testid="grant-delete" onClick={() => setConfirmDel(true)} className="px-3 py-1.5 text-[13px]">Delete grant</Button></div>}
      <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={del} title="Delete this grant?" message="This moves the grant to Trash, where it can be restored for 30 days before it is permanently removed." />

      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-wide text-ink/45">Open record</div>
          <h1 className="font-zilla text-3xl font-semibold">{g.title}</h1>
          <div className="text-[14px] text-ink/60">{g.funder || "—"}{SUBMITTED_OR_LATER.includes(g.stage) && g.stage_dates?.submitted ? ` · Submitted ${fmtCt(g.stage_dates.submitted)}` : ""}{g.lead ? ` · Lead: ${g.lead}` : ""}</div>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="stage-tabs">
          {ALL_STAGES.map(([k, lbl]) => (
            <button key={k} data-testid={`stage-tab-${k}`} disabled={!canEdit} onClick={() => setStage(k)}
              className={`px-3 py-1.5 rounded-lg text-[13px] font-semibold border ${g.stage === k ? "bg-ink text-cream border-ink" : "border-ink/15 text-ink/60 hover:border-ink/40"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* Requested / Awarded / Spent + Documents */}
      <div className="grid lg:grid-cols-4 gap-4 mb-4">
        {[["amount_requested", "Requested", "The ask on the application"], ["amount_awarded", "Awarded", "Leave blank until decided"], ["amount_spent", "Spent to date", "Manually tracked"]].map(([key, label, hint]) => (
          <Card key={key} className="!p-4">
            <div className="text-[11px] font-bold uppercase tracking-wide text-ink/45 mb-1">{label}</div>
            <div className="flex items-baseline gap-1">
              <span className="text-ink/50 text-xl">$</span>
              <input type="number" data-testid={`amt-${key}`} disabled={!canEdit} value={f[key] || 0}
                onChange={(e) => setF({ ...f, [key]: parseFloat(e.target.value) || 0 })} onBlur={() => save({})}
                className="font-zilla text-2xl font-semibold bg-transparent w-full focus:outline-none" />
            </div>
            <div className="text-[11px] text-ink/45 mt-1">{hint}</div>
          </Card>
        ))}
        <Card accent="#800000" className="!p-4 bg-ink text-cream" data-testid="grant-docs">
          <div className="text-[11px] font-bold uppercase tracking-wide text-cream/60 mb-1">Documents</div>
          <div className="text-[13px] mb-2">Drive folder: Grants / {g.title}</div>
          <div className="text-[12px] text-cream/60 mb-2">{(g.documents || []).length} file(s)</div>
          <label className="inline-flex items-center gap-1.5 bg-cream text-ink rounded-lg px-3 py-1.5 text-[13px] font-semibold cursor-pointer" data-testid="grant-upload">
            <Icon name="Upload" className="w-3.5 h-3.5" /> Upload files<input type="file" className="hidden" disabled={!canEdit} onChange={(e) => upload(e.target.files[0])} />
          </label>
        </Card>
      </div>

      {/* Details: real dates, grant lead, funder contacts, collaborating orgs */}
      <Card className="mb-4" data-testid="grant-details">
        <h3 className="font-zilla text-xl font-semibold mb-3">Details</h3>
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <Field label="Opened" hint="When the opportunity opens"><Input type="date" data-testid="grant-open-date" disabled={!canEdit} value={f.open_date || ""} onChange={(e) => setF({ ...f, open_date: e.target.value })} onBlur={() => save({})} /></Field>
          <Field label="LOI due" hint="Leave blank if none"><Input type="date" data-testid="grant-loi-date" disabled={!canEdit} value={f.loi_date || ""} onChange={(e) => setF({ ...f, loi_date: e.target.value })} onBlur={() => save({})} /></Field>
          <Field label="Application due" hint="Full application due"><Input type="date" data-testid="grant-submission-date" disabled={!canEdit} value={f.submission_date || f.deadline || ""} onChange={(e) => setF({ ...f, submission_date: e.target.value })} onBlur={() => save({})} /></Field>
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <Field label="Grant lead" hint="Link a real person, or add a new contact"><LeadPicker leadRef={f.lead_ref} leadName={f.lead} disabled={!canEdit} onChange={(ref) => save({ lead_ref: ref })} /></Field>
          <Field label="Collaborating organizations" hint="Partners working on this grant with us"><CollaboratorPicker value={f.collaborators} disabled={!canEdit} onChange={(arr) => save({ collaborators: arr })} /></Field>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[13px] font-semibold text-ink/70">Funder contacts</span>
            {canEdit && <button data-testid="funder-contact-add" onClick={() => setF({ ...f, funder_contacts: [...(f.funder_contacts || []), { name: "", email: "", phone: "" }] })} className="text-[13px] text-dark-olive font-semibold">+ Add another contact</button>}
          </div>
          <div className="space-y-2">
            {(f.funder_contacts || []).length === 0 && <p className="text-[13px] text-ink/45">No funder contacts yet.</p>}
            {(f.funder_contacts || []).map((c, i) => (
              <div key={i} className="grid sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-center" data-testid={`funder-contact-${i}`}>
                <Input data-testid={`funder-name-${i}`} disabled={!canEdit} value={c.name || ""} placeholder="Name" onChange={(e) => setFC(i, "name", e.target.value)} onBlur={() => save({})} />
                <Input data-testid={`funder-email-${i}`} disabled={!canEdit} value={c.email || ""} placeholder="Email" onChange={(e) => setFC(i, "email", e.target.value)} onBlur={() => save({})} />
                <Input data-testid={`funder-phone-${i}`} disabled={!canEdit} value={c.phone || ""} placeholder="Phone" onChange={(e) => setFC(i, "phone", e.target.value)} onBlur={() => save({})} />
                {canEdit && <button data-testid={`funder-remove-${i}`} onClick={() => { const next = (f.funder_contacts || []).filter((_, j) => j !== i); setF({ ...f, funder_contacts: next }); save({ funder_contacts: next }); }} className="text-ink/40 hover:text-maroon shrink-0"><Icon name="X" className="w-4 h-4" /></button>}
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* Drop zone */}
      <div ref={dropRef} data-testid="grant-dropzone" onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("border-maroon"); }}
        onDragLeave={() => dropRef.current?.classList.remove("border-maroon")} onDrop={onDrop}
        className="border-2 border-dashed border-ink/20 rounded-xl py-6 text-center text-ink/50 text-[13px] mb-4 transition-colors">
        Drop files here from your phone, computer, or external drive
        {(g.documents || []).length > 0 && <div className="flex flex-wrap justify-center gap-2 mt-2">{g.documents.map((d) => <a key={d.id} href={d.url} target="_blank" rel="noreferrer" className="text-dark-olive underline text-[13px]">{d.name}</a>)}</div>}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Narrative notes */}
        <Card>
          <h3 className="font-zilla text-xl font-semibold mb-1">Narrative notes</h3>
          <p className="text-[13px] text-ink/55 mb-3">Key language and figures worth keeping while you work on this grant.</p>
          <div className="space-y-3">
            {(f.narrative || []).map((s, i) => (
              <div key={s.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-semibold text-ink/70">{s.label}</span>
                  {canEdit && <button data-testid={`narr-del-${s.key}`} onClick={() => delSection(i)} className="text-[12px] text-ink/40 hover:text-maroon flex items-center gap-1"><Icon name="Trash2" className="w-3.5 h-3.5" /> Remove</button>}
                </div>
                <Textarea data-testid={`narr-${s.key}`} rows={3} disabled={!canEdit} value={s.text || ""} onChange={(e) => setNarr(i, e.target.value)} onBlur={() => save({})} placeholder={`${s.label}…`} />
              </div>
            ))}
          </div>
          {canEdit && (addingSec ? (
            <div className="flex gap-2 mt-3">
              <input autoFocus data-testid="section-label-input" value={secLabel} onChange={(e) => setSecLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSection()} placeholder="Section name this funder asks for…" className="flex-1 bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 min-h-[44px] text-[14px] focus:outline-none focus:border-maroon/50" />
              <Button data-testid="section-confirm" onClick={addSection}>Add</Button>
              <Button variant="secondary" onClick={() => { setAddingSec(false); setSecLabel(""); }}>Cancel</Button>
            </div>
          ) : (
            <Button variant="secondary" className="mt-3 w-full" data-testid="add-section" onClick={() => setAddingSec(true)}><Icon name="Plus" className="w-4 h-4" /> Add a section this funder asks for</Button>
          ))}
        </Card>

        {/* Notes log */}
        <Card>
          <h3 className="font-zilla text-xl font-semibold mb-1">Notes on this grant</h3>
          <p className="text-[13px] text-ink/55 mb-3">Anything worth remembering — a call with the program officer, a change in scope.</p>
          {canEdit && (
            <div className="mb-3">
              <Textarea data-testid="note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Type a note and press Add…" />
              <input data-testid="note-date" type="date" value={noteAt} onChange={(e) => setNoteAt(e.target.value)} className="w-full mt-2 bg-warm-cream border border-ink/20 rounded-lg px-3 py-2 text-[14px]" />
              <Button className="w-full mt-2" data-testid="add-note" onClick={addNote}>Add note</Button>
            </div>
          )}
          <div className="space-y-3">
            {(g.note_log || []).length === 0 && <p className="text-[13px] text-ink/50">No notes yet.</p>}
            {(g.note_log || []).map((n) => (
              editNote?.id === n.id ? (
                <div key={n.id} className="border-b border-ink/8 pb-2 last:border-0" data-testid={`note-editing-${n.id}`}>
                  <Textarea rows={2} data-testid={`note-edit-text-${n.id}`} value={editNote.text} onChange={(e) => setEditNote({ ...editNote, text: e.target.value })} />
                  <input type="date" data-testid={`note-edit-date-${n.id}`} value={editNote.at} onChange={(e) => setEditNote({ ...editNote, at: e.target.value })} className="mt-2 bg-warm-cream border border-ink/20 rounded-lg px-3 py-2 text-[14px]" />
                  <div className="flex gap-2 mt-2 items-center"><Button data-testid={`note-edit-save-${n.id}`} onClick={saveNote} className="text-[13px] px-3 py-1.5">Save</Button><button onClick={() => setEditNote(null)} className="text-[13px] text-ink/50 underline">Cancel</button></div>
                </div>
              ) : (
                <div key={n.id} className="border-b border-ink/8 pb-2 last:border-0" data-testid={`note-${n.id}`}>
                  <div className="text-[14px] whitespace-pre-wrap">{n.text}</div>
                  <div className="text-[12px] text-ink/50 mt-1 flex justify-between gap-2"><span>{n.by} · {fmt(n.at)}{n.edited_at && " · edited"}</span>{canEdit && <button data-testid={`note-edit-${n.id}`} onClick={() => setEditNote({ id: n.id, text: n.text, at: (n.at || "").slice(0, 10) })} className="text-dark-olive font-semibold hover:underline shrink-0">Edit</button>}</div>
                </div>
              )
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function NewGrantForm({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", funder: "", lead_ref: null, stage: "researching", amount_requested: 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required.");
    setBusy(true);
    try { await api.post("/grants", f); toast.success("Grant created"); onSaved(); }
    catch (e) { toast.error(errText(e) || "Could not create"); }
    finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New grant" data-testid="grant-form"
      footer={<Button data-testid="grant-save" onClick={save} disabled={busy}>{busy ? "Creating…" : "Create grant"}</Button>}>
      <div className="space-y-3">
        <Field label="Grant title"><Input data-testid="grant-title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
        <Field label="Funder"><Input data-testid="grant-funder" value={f.funder} onChange={(e) => setF({ ...f, funder: e.target.value })} /></Field>
        <Field label="Lead" hint="Link a person in the system or add a new contact"><LeadPicker leadRef={f.lead_ref} leadName="" onChange={(ref) => setF({ ...f, lead_ref: ref })} /></Field>
        <Field label="Amount requested ($)"><Input type="number" data-testid="grant-requested" value={f.amount_requested} onChange={(e) => setF({ ...f, amount_requested: parseFloat(e.target.value) || 0 })} /></Field>
        <Field label="Starting stage">
          <Select data-testid="grant-stage" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>{ALL_STAGES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>
        </Field>
      </div>
    </Modal>
  );
}

// Phone (below 900px): stage chips + one stage's grants as full-width cards; Move to stage sheet instead of dragging.
function MoveSheet({ g, onPick, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[70] flex flex-col justify-end min-[900px]:hidden" data-testid="grant-move-sheet">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-cream rounded-t-2xl p-4 pb-[calc(16px+env(safe-area-inset-bottom))] shadow-2xl animate-rise">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="flex-1 font-zilla text-xl font-semibold leading-snug" data-testid="grant-move-title">{g.title}</h2>
          <button onClick={onClose} aria-label="Close" data-testid="grant-move-close" className="w-11 h-11 rounded-xl border border-ink/15 flex items-center justify-center text-ink/70"><Icon name="X" className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {ALL_STAGES.map(([k, lbl]) => (
            <button key={k} onClick={() => onPick(k)} data-testid={`grant-move-to-${k}`}
              className={`min-h-[48px] px-3 rounded-xl border text-[15px] font-semibold ${g.stage === k ? "bg-maroon border-maroon text-cream" : "bg-white border-ink/15 text-ink"}`}>{lbl}</button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Next open deadline for the card badge: soonest upcoming, else the most recently missed.
function nextDeadline(g) {
  const early = ["researching", "drafting"].includes(g.stage);
  const late = ["awarded", "active_reporting"].includes(g.stage);
  const list = [
    early && { label: "LOI", value: g.loi_date },
    early && { label: "Application", value: g.submission_date || g.deadline },
    late && { label: "Report", value: g.deadline },
  ].filter((d) => d && d.value);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const withDays = list.map((d) => ({ ...d, days: Math.round((parseLocalDate(d.value) - today) / 86400000) }));
  const upcoming = withDays.filter((d) => d.days >= 0).sort((a, b) => a.days - b.days)[0];
  return upcoming || withDays.sort((a, b) => b.days - a.days)[0] || null;
}

function DeadlineBadge({ g }) {
  const d = nextDeadline(g);
  if (!d || d.days > 14) return null;
  const overdue = d.days < 0;
  const n = Math.abs(d.days);
  const text = overdue ? `Overdue · ${d.label} was due ${n} day${n === 1 ? "" : "s"} ago`
    : d.days === 0 ? `${d.label} due today` : `${d.label} due in ${n} day${n === 1 ? "" : "s"}`;
  return (
    <span data-testid={`m-grant-deadline-${g.grant_id}`} data-state={overdue ? "overdue" : "soon"}
      className={`flex w-fit items-center gap-1 mb-2 rounded-full px-2.5 py-1 text-[12px] font-semibold ${overdue ? "bg-maroon text-cream" : "bg-gold/30 text-ink"}`}>
      <Icon name={overdue ? "AlertTriangle" : "Clock"} className="w-3.5 h-3.5" />{text}
    </span>
  );
}

function PhoneGrantCard({ g, onOpen, canEdit, onMove }) {
  const entered = g.stage_dates?.[g.stage];
  return (
    <Card className="!p-4" data-testid={`m-grant-${g.grant_id}`}>
      <DeadlineBadge g={g} />
      <button onClick={() => onOpen(g.grant_id)} className="text-left font-semibold text-[16px] leading-snug text-ink hover:text-maroon" data-testid={`m-grant-open-${g.grant_id}`}>{g.title}</button>
      <div className="text-[14px] text-ink/65 mt-0.5">{g.funder || "No funder yet"}</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[13px] text-ink/70">
        <span className="font-semibold text-ink">{money(g.amount_awarded || g.amount_requested)}</span>
        {entered && <span>In {STAGE_LABELS[g.stage]} since {fmtCt(entered)}</span>}
        <span>Lead: {g.lead || "None"}</span>
      </div>
      {canEdit && <Button variant="secondary" onClick={() => onMove(g)} className="mt-3 w-full justify-center" data-testid={`m-grant-move-${g.grant_id}`}>Move to stage</Button>}
    </Card>
  );
}

function PhoneGrants({ items, onOpen, canEdit, onMoved }) {
  const count = (k) => items.filter((g) => g.stage === k).length;
  const ordered = [...ALL_STAGES.filter(([k]) => count(k)), ...ALL_STAGES.filter(([k]) => !count(k))];
  const [stage, setStage] = useState(() => (ordered[0] || ALL_STAGES[0])[0]);
  const [moving, setMoving] = useState(null);
  const list = items.filter((g) => g.stage === stage);
  const move = async (k) => {
    const g = moving;
    setMoving(null);
    if (k === g.stage) return;
    try { await api.put(`/grants/${g.grant_id}`, { ...g, stage: k }); toast.success(`Moved to ${STAGE_LABELS[k]}`); setStage(k); onMoved(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Could not move the grant"); }
  };
  return (
    <div className="min-[900px]:hidden mt-4" data-testid="grants-phone">
      <div className="text-[11px] uppercase tracking-widest font-bold text-ink/50 mb-2">Stage</div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4" data-testid="grant-stage-chips">
        {ordered.map(([k, lbl]) => (
          <button key={k} onClick={() => setStage(k)} data-testid={`grant-stage-chip-${k}`}
            className={`shrink-0 inline-flex items-center gap-2 px-3.5 min-h-[42px] rounded-xl border text-[14px] font-semibold ${stage === k ? "bg-maroon border-maroon text-cream" : "bg-white border-ink/15 text-ink"}`}>
            {lbl}<span className={`text-[12px] rounded-full px-1.5 ${stage === k ? "bg-cream/20" : "bg-ink/8"}`}>{count(k)}</span>
          </button>
        ))}
      </div>
      <div className="flex items-baseline gap-2 mt-3 mb-3">
        <h2 className="font-zilla text-xl font-semibold" data-testid="grant-stage-name">{STAGE_LABELS[stage]}</h2>
        <span className="text-[14px] text-ink/60" data-testid="grant-stage-count">{list.length} {list.length === 1 ? "grant" : "grants"}</span>
      </div>
      <div className="space-y-3">
        {list.map((g) => <PhoneGrantCard key={g.grant_id} g={g} onOpen={onOpen} canEdit={canEdit} onMove={setMoving} />)}
        {list.length === 0 && <Card className="text-center text-ink/55 py-8" data-testid="grant-stage-empty">No grants in this stage.</Card>}
      </div>
      {moving && <MoveSheet g={moving} onPick={move} onClose={() => setMoving(null)} />}
    </div>
  );
}

export default function GrantsAdmin({ me }) {
  const canEdit = me?.user?.is_super_admin || ["edit", "full"].includes(me?.permissions?.grants);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmDel, setConfirmDel] = useState(false);
  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulkDelete = async () => {
    const ids = [...selected];
    await Promise.all(ids.map((id) => api.delete(`/grants/${id}`).catch(() => null)));
    toast.success(`${ids.length} grant${ids.length === 1 ? "" : "s"} moved to Trash`);
    setSelected(new Set()); load();
  };
  useQuickAdd("grants", () => { if (canEdit) setCreating(true); });
  const load = useCallback(async () => { const r = await api.get("/grants"); setItems(r.data.items); setLoaded(true); }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const gid = sessionStorage.getItem("rca_open_grant");
    if (gid) { sessionStorage.removeItem("rca_open_grant"); setOpenId(gid); }
  }, []);

  if (openId) return <OpenRecord grantId={openId} canEdit={canEdit} onBack={() => setOpenId(null)} onChanged={load} />;

  const totals = items.reduce((a, g) => ({ req: a.req + (g.amount_requested || 0), awd: a.awd + (g.amount_awarded || 0), spent: a.spent + (g.amount_spent || 0) }), { req: 0, awd: 0, spent: 0 });
  const declined = items.filter((g) => g.stage === "declined");
  const columns = declined.length ? ALL_STAGES : BOARD;

  return (
    <div data-testid="grants-page">
      <ConfirmDelete open={confirmDel} onClose={() => setConfirmDel(false)} onConfirm={bulkDelete} title={`Delete ${selected.size} grant${selected.size === 1 ? "" : "s"}?`} message="This moves the selected grants to Trash, where they can be restored for 30 days before they are permanently removed." />
      {canEdit && selected.size > 0 && (
        <div className="flex items-center gap-3 bg-ink text-cream rounded-xl px-4 py-3 mb-3" data-testid="grants-bulk-bar">
          <span className="text-[14px] font-semibold">{selected.size} selected</span>
          <Button variant="danger" data-testid="grants-bulk-delete" onClick={() => setConfirmDel(true)} className="px-3 py-2 min-h-[38px] text-[13px] !text-cream !border-cream/40">Delete</Button>
          <button data-testid="grants-bulk-clear" onClick={() => setSelected(new Set())} className="text-[13px] underline text-cream/80 ml-auto">Clear</button>
        </div>
      )}
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <div>
          <h1 className="font-zilla text-3xl sm:text-4xl font-semibold">Grant Portal</h1>
          <p className="text-[15px] text-ink/70">{money(totals.req)} requested · {money(totals.awd)} awarded · {money(totals.spent)} spent to date</p>
        </div>
        <div className="grid grid-cols-2 gap-2 w-full min-[900px]:w-auto min-[900px]:flex">
          <a data-testid="grants-export-all" href={`${API}/grants/export`} className="border border-ink/25 rounded-lg px-4 py-2.5 min-h-[44px] text-[15px] font-semibold inline-flex items-center justify-center gap-1.5"><Icon name="Download" className="w-4 h-4" /> Export<span className="hidden min-[900px]:inline"> spreadsheet</span></a>
          {canEdit && <Button data-testid="new-grant-btn" onClick={() => setCreating(true)} className="justify-center"><Icon name="Plus" className="w-4 h-4" /> New grant</Button>}
        </div>
      </div>

      {loaded && items.length === 0 && <Card className="text-center text-ink/55 py-12 mt-4">No grants yet.</Card>}

      {loaded && <PhoneGrants items={items} onOpen={setOpenId} canEdit={canEdit} onMoved={load} />}

      <div className="hidden min-[900px]:grid gap-2 pb-2 mt-4" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }} data-testid="grants-board">
        {columns.map(([stage, label]) => {
          const col = items.filter((g) => g.stage === stage);
          return (
            <div key={stage} className="min-w-0" data-testid={`grant-col-${stage}`}>
              <div className="flex items-center justify-between gap-1 px-0.5 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-ink/60 truncate">{label}</span>
                <span className="text-[11px] font-semibold bg-ink/8 rounded-full px-1.5 py-0.5 shrink-0">{col.length}</span>
              </div>
              <div className="bg-warm-cream/50 border border-ink/10 rounded-xl p-1.5 space-y-2 min-h-[120px]">
                {col.map((g) => <GrantCard key={g.grant_id} g={g} onOpen={setOpenId} selectable={canEdit} selected={selected.has(g.grant_id)} onToggle={() => toggleSel(g.grant_id)} />)}
                {col.length === 0 && <div className="text-[12px] text-ink/35 text-center py-6">Empty</div>}
              </div>
            </div>
          );
        })}
      </div>

      {creating && <NewGrantForm onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}
